/**
 * Building Templates Registry
 *
 * Central registry for all building templates.
 * Templates are code-based functions that generate buildings
 * and units from form parameters.
 */

import { Building2, Layers, Warehouse, Grid3X3, Rows3, RectangleHorizontal, Pentagon, Tent } from 'lucide-react'
import type { TemplateType, TemplateInfo, AnyBuildingTemplate } from './types'
import {
  singleUnitCommunityTemplate,
  type SingleUnitParams,
} from './single-unit-community'
import {
  SingleRowTemplate,
  type SingleRowParams,
} from './single-row'
import {
  DoubleRowTemplate,
  type DoubleRowParams,
} from './double-row'
import {
  outdoorRectangularTemplate,
  type OutdoorRectangularParams,
} from './outdoor-rectangular'
import {
  StandardTHangarTemplate,
  type THangarParams,
} from './standard-t-hangar'
import {
  NestedTHangarTemplate,
  type NestedTHangarParams,
} from './nested-t-hangar'
import {
  ArchHangarTemplate,
  type ArchHangarParams,
} from './arch-hangar'

// Re-export types
export * from './types'
export * from './single-unit-community'
export * from './outdoor-rectangular'
export * from './arch-hangar'

// The row/T-hangar template modules each define their own `generateBuilding`,
// `generateUnits` and `calculateBuildingDepth`. Star-exporting them here made those
// names ambiguous (TS2308), so the barrel resolved them to nothing. Import those from
// the template's own `./generate` / `./params` module; only the template objects are
// re-exported here.
export { SingleRowTemplate } from './single-row'
export { DoubleRowTemplate } from './double-row'
export { StandardTHangarTemplate } from './standard-t-hangar'
export { NestedTHangarTemplate } from './nested-t-hangar'

/** All available templates */
export const templates: Record<TemplateType, AnyBuildingTemplate> = {
  single_unit_community: singleUnitCommunityTemplate,
  single_row: SingleRowTemplate,
  double_row: DoubleRowTemplate,
  outdoor_rectangular: outdoorRectangularTemplate,
  // Future outdoor templates - placeholders
  outdoor_row: null as unknown as AnyBuildingTemplate,
  outdoor_grid: null as unknown as AnyBuildingTemplate,
  outdoor_polygon: null as unknown as AnyBuildingTemplate,
  // T-Hangar templates
  standard_t_hangar: StandardTHangarTemplate,
  nested_t_hangar: NestedTHangarTemplate,
  // Arch hangar
  arch_hangar: ArchHangarTemplate,
}

/** Template info for hangar selector UI (hangars only) */
export const templateInfo: TemplateInfo[] = [
  {
    id: 'single_unit_community',
    name: 'Single Unit Community',
    description: 'One building = one unit. Simple open floor plan.',
    icon: Building2,
    buildingType: 'single_unit_hangar',
    available: true,
  },
  {
    id: 'single_row',
    name: 'Single Row Box Hangar',
    description: 'Row of 1-20 box units with shared partition walls.',
    icon: Rows3,
    buildingType: 'box_hangar_single_row',
    available: true,
  },
  {
    id: 'double_row',
    name: 'Double Row (Back-to-Back)',
    description: 'Two rows sharing a spine wall, duplex-style.',
    icon: Layers,
    buildingType: 'box_hangar_double_row',
    available: true,
  },
  {
    id: 'standard_t_hangar',
    name: 'Standard T-Hangar',
    description: 'Linear row of T-shaped units for GA aircraft.',
    icon: Warehouse,
    buildingType: 't_hangar_row',
    available: true,
  },
  {
    id: 'nested_t_hangar',
    name: 'Nested T-Hangar',
    description: 'Two interlocked T-hangar rows, space-efficient.',
    icon: Grid3X3,
    buildingType: 'nested_t_hangar_row',
    available: true,
  },
  {
    id: 'arch_hangar',
    name: 'Arch Hangar (Quonset)',
    description: 'Semi-cylindrical arch-span building, no internal columns.',
    icon: Tent,
    buildingType: 'arch_hangar',
    available: true,
  },
]

/** Template info for outdoor area selector UI */
export const outdoorTemplateInfo: TemplateInfo[] = [
  {
    id: 'outdoor_rectangular',
    name: 'Rectangular Area',
    description: 'Flat open-air rectangular area for ramp or tiedown parking.',
    icon: RectangleHorizontal,
    buildingType: 'ramp_area',
    available: true,
  },
  {
    id: 'outdoor_row',
    name: 'Outdoor Row',
    description: 'Row of tiedown spots with numbered positions.',
    icon: Rows3,
    buildingType: 'ramp_area',
    available: false,
  },
  {
    id: 'outdoor_grid',
    name: 'Outdoor Grid',
    description: 'Grid layout of parking spots for dense ramp areas.',
    icon: Grid3X3,
    buildingType: 'ramp_area',
    available: false,
  },
  {
    id: 'outdoor_polygon',
    name: 'Custom Polygon',
    description: 'Free-form polygon for irregular outdoor areas.',
    icon: Pentagon,
    buildingType: 'ramp_area',
    available: false,
  },
]

/** Get template by ID */
export function getTemplate(id: TemplateType): AnyBuildingTemplate | null {
  const template = templates[id]
  return template || null
}

/** Get template info by ID (searches both hangar and outdoor) */
export function getTemplateInfo(id: TemplateType): TemplateInfo | undefined {
  return templateInfo.find((t) => t.id === id) || outdoorTemplateInfo.find((t) => t.id === id)
}

/** Get outdoor template info by ID */
export function getOutdoorTemplateInfo(id: TemplateType): TemplateInfo | undefined {
  return outdoorTemplateInfo.find((t) => t.id === id)
}

/** Get all available templates */
export function getAvailableTemplates(): TemplateInfo[] {
  return templateInfo.filter((t) => t.available)
}

/** Check if a template is available */
export function isTemplateAvailable(id: TemplateType): boolean {
  const info = getTemplateInfo(id)
  return info?.available ?? false
}

/** Get single unit community template with proper typing */
export function getSingleUnitTemplate() {
  return singleUnitCommunityTemplate
}

/** Get single row template with proper typing */
export function getSingleRowTemplate() {
  return SingleRowTemplate
}

/** Get double row template with proper typing */
export function getDoubleRowTemplate() {
  return DoubleRowTemplate
}

/** Get outdoor rectangular template with proper typing */
export function getOutdoorRectangularTemplate() {
  return outdoorRectangularTemplate
}

/** Get standard T-hangar template with proper typing */
export function getStandardTHangarTemplate() {
  return StandardTHangarTemplate
}

/** Get nested T-hangar template with proper typing */
export function getNestedTHangarTemplate() {
  return NestedTHangarTemplate
}

/** Get arch hangar template with proper typing */
export function getArchHangarTemplate() {
  return ArchHangarTemplate
}

export type { SingleUnitParams, SingleRowParams, DoubleRowParams, OutdoorRectangularParams, THangarParams, NestedTHangarParams, ArchHangarParams }
